import { Constants } from "./constants";
import { StandardAltitudes } from "./enums";
import { IAllDayThermicIndexModel, IThermicIndexResult } from "./interfaces";
import { ThermicIndexParametersModel, ThermicIndexResultComments, ThermicIndexResultModel } from "./models";

export class Helper {

  static getThermicIndex(parametersModel: ThermicIndexParametersModel, isFeet: boolean): IThermicIndexResult {
    const { altitude700T, altitude850T, dailyParameters, availableAltitude, hourlyTempModelList, hourlyWindModelList,
      windSpeed850hPa, windDirection850hPa, hourlyCloudCoverModelList,
      cloudCover, cloudCoverLow, cloudCoverMid, cloudCoverHigh, hourlyTemperature_2m, hourlySurfaceT, hourlyConvectiveCloudBase,
      hourlyCape, hourlyRelativeHumidityModelList, hoursList, hourlyDewPoint } = parametersModel;

    const { altitude700Index, altitude850Index } = Helper.getIndex(altitude700T, altitude850T, hourlySurfaceT, availableAltitude, isFeet);

    const indexComment = ThermicIndexResultComments.getIndexComment(altitude700Index, altitude850Index, windSpeed850hPa);

    const allDayIndexes = hourlyTempModelList.map(t => {
      const { altitude700Index, altitude850Index } = Helper.getIndex(t.temperature_700hPa, t.temperature_850hPa, t.hourlySurfaceT, availableAltitude, isFeet);
      const hourlyWindSpeed850hPa = hourlyWindModelList.find(w => w.hour === t.hour).windSpeed_850hPa;
      const hourlyGust = hourlyWindModelList.find(w => w.hour === t.hour).windGusts10m;
      return {
        altitudeHourly700Index: altitude700Index,
        altitudeHourly850Index: altitude850Index,
        hour: t.hour,
        indexComment: ThermicIndexResultComments.getIndexComment(altitude700Index, altitude850Index, hourlyWindSpeed850hPa)
        , hourlyGust: hourlyGust
      } as IAllDayThermicIndexModel;
    });

    hourlyWindModelList.forEach(hw => {
      hw.windDirectionName850hPa = this.getWindDirectionText(hw.windDirection_850hPa);
    });

    return {
      altitude: availableAltitude,
      hourlyIndexModelList: allDayIndexes,
      dailyParameters: dailyParameters,
      altitude700Index,
      altitude850Index,
      indexComment, hourlyWindModelList,
      windSpeed850hPa,
      windDirection850hPa,
      hourlyCloudCoverModelList,
      cloudCover,
      cloudCoverLow,
      cloudCoverMid,
      cloudCoverHigh,
      hourlyTemperature_2m,
      hourlySurfaceT,
      hourlyConvectiveCloudBase,
      hourlyCape,
      hourlyRelativeHumidityModelList,
      hoursList,
      hourlyTempModelList,
      hourlyGust: allDayIndexes[0].hourlyGust, hourlyDewPoint: hourlyDewPoint
    } as IThermicIndexResult;
  }



  private static getIndex(altitude700T: number, altitude850T: number, maxGroundT: number, availableAltitude: number, isFeet: boolean) {
    let altitude700Index = altitude700T -
      (maxGroundT - ((this.getAltitude(isFeet, StandardAltitudes.sevenHundred) - availableAltitude) / 1000) * (isFeet ? Constants.DalrF : Constants.DalrC));
    let altitude850Index = altitude850T -
      (maxGroundT - ((this.getAltitude(isFeet, StandardAltitudes.eightHundredFifty) - availableAltitude) / 1000) * (isFeet ? Constants.DalrF : Constants.DalrC));
    return { altitude700Index, altitude850Index };
  }

  static getAltitude(isFeet: boolean, standardAltitude: StandardAltitudes) {
    if (isFeet) {
      switch (standardAltitude) {
        case StandardAltitudes.sevenHundred:
          return 10000;
        case StandardAltitudes.eightHundredFifty:
          return 5000;
      }
    } else {
      switch (standardAltitude) {
        case StandardAltitudes.sevenHundred:
          return 3000;
        case StandardAltitudes.eightHundredFifty:
          return 1500;
      }
    }
  }

  static getWindDirectionText(windDirection: number): string {
    // Normalize windDirection to range [0, 360)
    windDirection = windDirection % 360;

    if ((windDirection >= 348.75 && windDirection <= 360) || (windDirection >= 0 && windDirection < 11.25)) {
      return 'K';    // Kuzey
    } else if (windDirection >= 11.25 && windDirection < 33.75) {
      return 'KKD';  // Kuzey-Kuzeydoğu
    } else if (windDirection >= 33.75 && windDirection < 56.25) {
      return 'KD';   // Kuzeydoğu
    } else if (windDirection >= 56.25 && windDirection < 78.75) {
      return 'DKD';  // Doğu-Kuzeydoğu
    } else if (windDirection >= 78.75 && windDirection < 101.25) {
      return 'D';    // Doğu
    } else if (windDirection >= 101.25 && windDirection < 123.75) {
      return 'DGD';  // Doğu-Güneydoğu
    } else if (windDirection >= 123.75 && windDirection < 146.25) {
      return 'GD';   // Güneydoğu
    } else if (windDirection >= 146.25 && windDirection < 168.75) {
      return 'GGD';  // Güney-Güneydoğu
    } else if (windDirection >= 168.75 && windDirection < 191.25) {
      return 'G';    // Güney
    } else if (windDirection >= 191.25 && windDirection < 213.75) {
      return 'GGB';  // Güney-Güneybatı
    } else if (windDirection >= 213.75 && windDirection < 236.25) {
      return 'GB';   // Güneybatı
    } else if (windDirection >= 236.25 && windDirection < 258.75) {
      return 'BGB';  // Batı-Güneybatı
    } else if (windDirection >= 258.75 && windDirection < 281.25) {
      return 'B';    // Batı
    } else if (windDirection >= 281.25 && windDirection < 303.75) {
      return 'BKB';  // Batı-Kuzeybatı
    } else if (windDirection >= 303.75 && windDirection < 326.25) {
      return 'KB';   // Kuzeybatı
    } else if (windDirection >= 326.25 && windDirection < 348.75) {
      return 'KKB';  // Kuzey-Kuzeybatı
    }

    return ''; // Fallback
  }


  static getSpeedTextCss(windSpeed: number): string {
    var cssClass = 'text-secondary';
    if (windSpeed >= 30) {
      cssClass = 'text-warning bg-danger'
    } else if (windSpeed >= 25 && windSpeed < 30) {
      cssClass = 'text-danger'
    } else if (windSpeed >= 20 && windSpeed < 25) {
      cssClass = 'text-warning'
    } else if (windSpeed >= 10 && windSpeed < 20) {
      cssClass = 'text-success'
    } else if (windSpeed >= 0 && windSpeed < 10) {
      cssClass = 'text-primary'
    }
    return cssClass;
  }

  static pad(n) {
    return (n < 10) ? ("0" + n) : n;
  }
  static getPrecipitationTypeLabel(code: number, precipitationAmount: number): string {
    switch (code) {
      case 0: if (typeof precipitationAmount === 'number' && !isNaN(precipitationAmount)) {
        return 'Yok';
      };
      case 1: return 'Yağmur';
      case 3: return 'Dolu';
      case 5: return 'Kar';
      case 6: return 'Sulu Kar';
      case 7: return 'Karla Karışık Yağmur';
      case 8: return 'Buz Tanecikleri';
      case 12: return 'Donmuş Çise';
      default: return 'Yok';
    }
  }

}


